import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Plus, Download, Clock, Edit, Trash2, Code, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TYPE_LABELS = {
  offer_letter: 'Offer Letter',
  experience_letter: 'Experience Letter',
  relieving_letter: 'Relieving Letter',
  salary_certificate: 'Salary Certificate',
  custom: 'Custom',
};

export default function HRLettersPage() {
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('generate');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [form, setForm] = useState({ name: '', letter_type: 'custom', content: '' });
  const [genTemplateId, setGenTemplateId] = useState('');
  const [genEmployeeId, setGenEmployeeId] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, e, h, p] = await Promise.all([
        api.get('/api/hr-letters/templates'),
        api.get('/api/employees'),
        api.get('/api/hr-letters/history'),
        api.get('/api/hr-letters/placeholders'),
      ]);
      setTemplates(t.data);
      setEmployees(e.data?.employees || []);
      setHistory(h.data);
      setPlaceholders(p.data?.placeholders || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveTemplate = async () => {
    if (!form.name || !form.content) { toast.error('Name and content required'); return; }
    try {
      if (editTemplate) {
        await api.put(`/api/hr-letters/templates/${editTemplate.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/api/hr-letters/templates', form);
        toast.success('Template created');
      }
      setTemplateOpen(false);
      setEditTemplate(null);
      setForm({ name: '', letter_type: 'custom', content: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/api/hr-letters/templates/${id}`);
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleGenerate = async () => {
    if (!genTemplateId || !genEmployeeId) { toast.error('Select template and employee'); return; }
    setGenerating(true);
    try {
      const response = await api.post(`/api/hr-letters/generate/${genTemplateId}/${genEmployeeId}`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.split('filename=')[1] || 'letter.pdf';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Letter generated and downloaded');
      load();
    } catch (e) { toast.error('Failed to generate letter'); }
    setGenerating(false);
  };

  const openEditTemplate = (t) => {
    setEditTemplate(t);
    setForm({ name: t.name, letter_type: t.letter_type, content: t.content });
    setTemplateOpen(true);
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-12" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-5" data-testid="hr-letters-page">
      <div>
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// HR LETTERS</Badge>
        <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">HR Letter Generation</h1>
        <p className="text-sm text-slate-500">Generate offer letters, experience certificates and more</p>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-white border border-slate-200 h-10">
          <TabsTrigger value="generate" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-generate">
            <Download className="w-3.5 h-3.5" />Generate
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-templates">
            <FileText className="w-3.5 h-3.5" />Templates
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-history">
            <Clock className="w-3.5 h-3.5" />History
          </TabsTrigger>
        </TabsList>

        {/* === GENERATE TAB === */}
        <TabsContent value="generate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader className="py-3 px-4 pb-2">
                <CardTitle className="text-sm font-semibold">Generate Letter</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-4">
                <div>
                  <Label>Select Template</Label>
                  <Select value={genTemplateId} onValueChange={setGenTemplateId}>
                    <SelectTrigger data-testid="gen-template-select"><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Select Employee</Label>
                  <Select value={genEmployeeId} onValueChange={setGenEmployeeId}>
                    <SelectTrigger data-testid="gen-employee-select"><SelectValue placeholder="Choose an employee..." /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {genTemplateId && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono text-xs" data-testid="template-preview">
                    {templates.find(t => t.id === genTemplateId)?.content || ''}
                  </div>
                )}
                <Button
                  onClick={handleGenerate}
                  disabled={!genTemplateId || !genEmployeeId || generating}
                  className="bg-[#0D2850] hover:bg-[#0B2246] gap-1 w-full"
                  data-testid="generate-btn"
                >
                  <Download className="w-4 h-4" />{generating ? 'Generating...' : 'Generate & Download PDF'}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="py-3 px-4 pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Code className="w-4 h-4 text-slate-500" /> Available Placeholders
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-1.5">
                  {placeholders.map(p => (
                    <div key={p.key} className="flex items-start gap-2">
                      <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-[#0D2850] font-mono shrink-0">{p.key}</code>
                      <span className="text-[10px] text-slate-500">{p.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === TEMPLATES TAB === */}
        <TabsContent value="templates" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">Letter Templates</CardTitle>
              <Dialog open={templateOpen} onOpenChange={(v) => { setTemplateOpen(v); if (!v) { setEditTemplate(null); setForm({ name: '', letter_type: 'custom', content: '' }); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" data-testid="create-template-btn"><Plus className="w-4 h-4" />New Template</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Template Name</Label>
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Internship Letter" data-testid="template-name-input" />
                      </div>
                      <div>
                        <Label>Type</Label>
                        <Select value={form.letter_type} onValueChange={v => setForm({ ...form, letter_type: v })}>
                          <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="offer_letter">Offer Letter</SelectItem>
                            <SelectItem value="experience_letter">Experience Letter</SelectItem>
                            <SelectItem value="relieving_letter">Relieving Letter</SelectItem>
                            <SelectItem value="salary_certificate">Salary Certificate</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Template Content</Label>
                      <p className="text-[10px] text-slate-400 mb-1">Use placeholders like {'{{employee_name}}'}, {'{{designation}}'}, etc.</p>
                      <Textarea
                        value={form.content}
                        onChange={e => setForm({ ...form, content: e.target.value })}
                        rows={15}
                        className="font-mono text-xs"
                        data-testid="template-content-input"
                      />
                    </div>
                    <Button onClick={handleSaveTemplate} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="save-template-btn">
                      {editTemplate ? 'Update Template' : 'Save Template'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                      <TableCell className="text-sm font-medium">{t.name}</TableCell>
                      <TableCell><Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">{TYPE_LABELS[t.letter_type] || t.letter_type}</Badge></TableCell>
                      <TableCell><Badge className={`text-[10px] ${t.is_default ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'} hover:opacity-90`}>{t.is_default ? 'Default' : 'Custom'}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{t.created_at?.split('T')[0]}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditTemplate(t)} data-testid={`edit-template-${t.id}`}>
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          {!t.is_default && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === HISTORY TAB === */}
        <TabsContent value="history" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-4 pb-2">
              <CardTitle className="text-sm font-semibold">Generation History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {history.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No letters generated yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Generated By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map(h => (
                      <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                        <TableCell className="text-sm font-medium">{h.employee_name}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono">{h.employee_code}</TableCell>
                        <TableCell><Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">{h.template_name}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-600">{h.generated_by}</TableCell>
                        <TableCell className="text-xs text-slate-500">{h.generated_at?.split('T')[0]}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
